/* eslint-disable jsx-a11y/alt-text */
import React, { useContext, useEffect, useRef, useState } from "react";
import { Loader } from "../../constents/components";
import arrow from "../../Assets/arrow.png";
import playIcon from "../../Assets/playIcon.svg";
import { InterviewContext } from "../../Contexts/InterviewContext";
import useRecordInterview from "../../Hooks/Interview/useRecordInterview";
import crossIcon from "../../Assets/close.svg";
import WarningIcon from "../../Assets/svgs/Interview/WarningIcon.svg";
import { useNavigate } from "react-router-dom";

const Interview = ({ questionData }) => {
  const navigate = useNavigate();
  const {
    applicantId,
    jobId,
    questionIndex,
    updateQuestionIndex,
    updateInterviewStatusToEnd,
  } = useContext(InterviewContext);
  const questionNumber = questionIndex + 1;

  // New response shape: { question, currentQuestion, total_questions }
  const questionText = questionData?.question;
  const noOfQuestions = questionData?.total_questions;
  const isLastQuestion = questionNumber === noOfQuestions;

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showConfirmationPopup, setShowConfirmationPop] = useState(false);
  const [isNextDisabled, setIsNextDisabled] = useState(true);
  const [timeLeft, setTimeLeft] = useState(180);

  const [interviewSubmiter, endInterview, isUploading] = useRecordInterview({
    applicantId,
    jobId,
    questionNumber,
    onSucess: updateQuestionIndex,
    onInterviewEnd: updateInterviewStatusToEnd,
  });

  // TTS — speak question on mount
  const speakQuestion = (text) => {
    window.speechSynthesis.cancel();
    const utt = new SpeechSynthesisUtterance(text);
    window.speechSynthesis.speak(utt);
  };

  useEffect(() => {
    if (questionText) speakQuestion(questionText);
    return () => window.speechSynthesis.cancel();
  }, [questionText]);

  // Back / refresh → warn user (browser shows native dialog; on back → redirect to /jobs)
  useEffect(() => {
    const onBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = "";
    };
    const onPopState = () => navigate("/jobs", { replace: true });
    window.addEventListener("beforeunload", onBeforeUnload);
    window.addEventListener("popstate", onPopState);
    return () => {
      window.removeEventListener("beforeunload", onBeforeUnload);
      window.removeEventListener("popstate", onPopState);
    };
  }, [navigate]);

  // 3-second buffer before enabling next/submit (MorphCast startup)
  useEffect(() => {
    const id = setTimeout(() => setIsNextDisabled(false), 3000);
    return () => clearTimeout(id);
  }, []);

  // Countdown timer — auto-submit on 0
  const timerRef = useRef(null);
  useEffect(() => {
    setTimeLeft(180);
    timerRef.current = setInterval(() => {
      setTimeLeft((pre) => {
        if (pre <= 1) {
          clearInterval(timerRef.current);
          handleNext();
          return 0;
        }
        return pre - 1;
      });
    }, 1000);
    return () => clearInterval(timerRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questionNumber]);

  const handleNext = () => {
    window.speechSynthesis.cancel();
    if (isLastQuestion) {
      setIsSubmitting(true);
      endInterview();
    } else {
      interviewSubmiter();
    }
  };

  const handleSubmitInterview = () => {
    setIsSubmitting(true);
    window.speechSynthesis.cancel();
    endInterview();
  };

  // Submitting overlay — shown when last question submit is clicked or while upload is in progress
  if (isSubmitting || isUploading) {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-black/40 backdrop-blur-sm z-[999999]">
        <div className="bg-white rounded-2xl p-8 flex flex-col items-center gap-4 max-w-sm w-full mx-4">
          <h2 className="text-xl font-semibold text-center">
            Submitting your response…
          </h2>
          <p className="text-gray-500 text-sm text-center">
            Please do not close or refresh this page.
          </p>
          <Loader />
        </div>
      </div>
    );
  }

  return (
    <>
      {showConfirmationPopup && (
        <div className="fixed inset-0 z-[999999] flex items-center justify-center bg-black/20 backdrop-blur-sm p-4">
          <div className="bg-white rounded-lg shadow-lg max-w-md w-full p-6">
            <div className="flex flex-col gap-6">
              <div className="flex justify-end">
                <img
                  onClick={() => setShowConfirmationPop(false)}
                  className="cursor-pointer w-6 h-6 hover:opacity-70"
                  src={crossIcon}
                  alt="Close"
                />
              </div>
              <div className="flex justify-center">
                <img className="w-32 h-32" src={WarningIcon} alt="Warning" />
              </div>
              <div className="text-center space-y-6">
                <p className="text-gray-700 text-lg">
                  Are you sure you want to submit the interview before answering
                  all questions?
                </p>
                <div className="flex gap-4">
                  <button
                    className="flex-1 px-4 py-2 bg-[#FF5D0E] text-white rounded-lg hover:bg-orange-600 transition-colors"
                    onClick={handleSubmitInterview}
                  >
                    Yes
                  </button>
                  <button
                    className="flex-1 px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-colors"
                    onClick={() => setShowConfirmationPop(false)}
                  >
                    No
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="bg-gray-100 min-h-screen p-2 lg:p-8">
        <div className="w-full lg:w-[70vw] mx-auto bg-white rounded-lg p-6">
          <div className="flex justify-between items-center mb-6">
            <div>
              <h1 className="text-2xl sm:text-3xl font-semibold text-gray-800">
                AI Interview
              </h1>
              <p className="mt-1 text-sm text-gray-500">
                Answer each question to complete your interview.
              </p>
            </div>
            <span className="text-lg font-semibold">
              Time Left: {timeLeft}s
            </span>
          </div>

          {/* Progress track */}
          <div className="flex justify-between items-center mb-3">
            <span className="text-sm font-medium text-gray-700">
              Question {questionNumber} of {noOfQuestions}
            </span>
            <span className="text-sm text-gray-500">
              {noOfQuestions > 0
                ? Math.round((questionNumber / noOfQuestions) * 100)
                : 0}
              % completed
            </span>
          </div>

          {/* Question section */}
          <div className="mb-10">
            <div className="flex items-center justify-between mb-8">
              <h2 className="text-xl text-gray-700">
                # Question {questionNumber}
              </h2>
              <button
                className="flex items-center gap-2 px-4 py-2 bg-[#FF5D0E] text-white rounded-full hover:bg-orange-600"
                onClick={() => speakQuestion(questionText)}
              >
                <img src={playIcon} alt="Play" className="w-4 h-4" />
                Replay Question
              </button>
            </div>

            <div className="bg-gray-50 p-8 rounded-lg mb-8">
              <h3 className="text-3xl text-center font-normal text-gray-800">
                {questionText}
              </h3>
            </div>

            <div className="flex justify-between items-center px-4 flex-col lg:flex-row gap-4">
              {/* Early exit — only on non-last questions */}
              {!isLastQuestion ? (
                <button
                  className="w-fit px-3 py-1 rounded-full bg-red-900 text-white flex items-center justify-center hover:bg-red-800"
                  onClick={() => setShowConfirmationPop(true)}
                >
                  Submit Interview
                </button>
              ) : (
                <span />
              )}

              {/* Recording indicator */}
              <div
                className="inline-flex items-center gap-2 rounded-full
             bg-red-50 px-3 py-2 text-sm font-medium text-red-700"
                role="status"
                aria-live="polite"
              >
                <span className="h-2 w-2 rounded-full bg-red-500 animate-pulse" />
                Recording in Progress
              </div>

              {/* Last question → Submit button | Other questions → Next arrow */}
              {isLastQuestion ? (
                <button
                  onClick={handleSubmitInterview}
                  disabled={isNextDisabled || isUploading}
                  className={`px-6 py-3 rounded-full text-white font-semibold transition-colors ${
                    isNextDisabled || isUploading
                      ? "bg-gray-300 cursor-not-allowed"
                      : "bg-[#FF5D0E] hover:bg-orange-600"
                  }`}
                >
                  Submit Interview
                </button>
              ) : (
                // New Next Question button goes here
                <button
                  type="button"
                  onClick={handleNext}
                  disabled={isNextDisabled || isUploading}
                  className={`inline-flex items-center justify-center gap-2
            rounded-lg px-6 py-3 min-h-12
            text-sm font-semibold text-white transition-colors
            focus-visible:outline-none focus-visible:ring-2
            focus-visible:ring-orange-500 focus-visible:ring-offset-2
            ${
              isNextDisabled || isUploading
                ? "bg-gray-300 cursor-not-allowed"
                : "bg-[#FF5D0E] hover:bg-orange-600 active:bg-orange-700"
            }`}
                >
                  Next Question
                  <span aria-hidden="true">→</span>
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Interview;
